import type { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import { AgentReferenceType, type ClientAgentReference } from '@/agent/references/store/schemas'
import { resolveUserFile } from '@/user_files/files/utils/resolve-user-files'
import { buildAgentReferenceViewerSession } from './build-agent-reference-viewer-session'

type OpenAgentReferenceViewerArgs = {
  reference: ClientAgentReference
  matterId?: string | null
  citationHighlightIndex?: number
  defaultTab?: CaseLawReferenceTab
}

export type OpenAgentReferenceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      reason: 'missing_file_reference' | 'missing_user_file' | 'missing_viewable_content' | 'unsupported_content_type' | 'unavailable_reference'
      error: string
    }

function unavailableReference(): OpenAgentReferenceViewerResult {
  return {
    opened: false,
    reason: 'unavailable_reference',
    error: 'Could not load this reference.',
  }
}

function missingFileReference(): OpenAgentReferenceViewerResult {
  return {
    opened: false,
    reason: 'missing_file_reference',
    error: 'This file reference is no longer available.',
  }
}

export async function openAgentReferenceViewer(args: OpenAgentReferenceViewerArgs): Promise<OpenAgentReferenceViewerResult> {
  const { reference, matterId, citationHighlightIndex, defaultTab } = args

  const session = buildAgentReferenceViewerSession({ reference, matterId, citationHighlightIndex, defaultTab })
  if (session) return { opened: true, session }

  if (reference.type === AgentReferenceType.FILE_UPLOAD_CHUNK || reference.type === AgentReferenceType.FILE_UPLOAD_FULL) {
    return missingFileReference()
  }

  if (reference.type !== AgentReferenceType.USER_FILE_QDRANT_CHUNK && reference.type !== AgentReferenceType.ASSET_EVENT) {
    return unavailableReference()
  }

  const metadata = reference.metadata as Record<string, unknown>
  const metadataUserFileId = typeof metadata.user_file_id === 'string' ? metadata.user_file_id : null
  const userFileId = reference.file_reference?.user_file_id ?? metadataUserFileId
  if (!userFileId) {
    return missingFileReference()
  }

  if (reference.file_reference?.viewable_content) {
    return {
      opened: false,
      reason: 'unsupported_content_type',
      error: "This file type can't be previewed.",
    }
  }

  const userFile = await resolveUserFile(userFileId)
  if (!userFile) {
    return {
      opened: false,
      reason: 'missing_user_file',
      error: 'This file reference is no longer available.',
    }
  }

  if (!userFile.viewable_content) {
    return {
      opened: false,
      reason: 'missing_viewable_content',
      error: "This file isn't ready to view yet.",
    }
  }

  const sessionWithUserFile = buildAgentReferenceViewerSession({ reference, matterId, citationHighlightIndex, defaultTab, userFile })

  if (sessionWithUserFile) return { opened: true, session: sessionWithUserFile }

  const viewableContentType = userFile.viewable_content.content_type
  if (viewableContentType) {
    return {
      opened: false,
      reason: 'unsupported_content_type',
      error: "This file type can't be previewed.",
    }
  }

  return unavailableReference()
}
